/**
 * One goal: a dated outcome ("VSL live and converting" by Oct 25), who owns
 * it, the traffic source it's for and how many of its tasks are done. Open
 * it to change it, see its tasks and add more.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { CheckCircle2, ChevronDown, Flag, Plus, RotateCcw, Trash2, XCircle } from "lucide-react";
import { api, ApiError } from "@/lib/api";
import { Avatar, Button, Select, Skeleton, Textarea, useConfirm, useToast } from "@/components/ui";
import { cn } from "@/lib/utils";
import { Card, Pill, dayLabel, dotClass, dueLabel, firstName } from "@/features/command/ui";
import { Field, PersonPills } from "../fields";
import { TaskRow } from "../TaskRow";
import { DayPicker } from "../DayPicker";
import { joinDue, sourceOptions, todayYmd } from "../lib";
import type { Goal, Person, SourceLite, TeamData, WorkTask } from "../types";

export function GoalCard({
  goal, open, onToggleOpen, onChanged, onOpenTask, people, sources, team, wsTz, taskVersion,
}: {
  goal: Goal;
  open: boolean;
  onToggleOpen: () => void;
  onChanged: () => void;
  onOpenTask: (t: WorkTask | { isNew: true; defaults: Partial<WorkTask> }) => void;
  people: Person[];
  sources: SourceLite[];
  team: TeamData | null;
  wsTz: string;
  /** Bumped by the page when a task changed in the drawer, to refetch. */
  taskVersion: number;
}) {
  const toast = useToast();
  const confirm = useConfirm();
  const { pathname } = useLocation();
  const [tasks, setTasks] = useState<WorkTask[] | null>(null);
  const [title, setTitle] = useState(goal.title);
  const [desc, setDesc] = useState(goal.description ?? "");
  const [newTask, setNewTask] = useState("");
  const [adding, setAdding] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => { setTitle(goal.title); setDesc(goal.description ?? ""); }, [goal.title, goal.description]);

  const loadTasks = useCallback(async () => {
    try {
      const d = await api.get<{ tasks: WorkTask[] }>("/tasks", { goal_id: goal.id, limit: 200 });
      setTasks(d.tasks);
    } catch {
      setTasks([]);
    }
  }, [goal.id]);

  useEffect(() => { if (open) void loadTasks(); }, [open, loadTasks, taskVersion]);
  useEffect(() => { if (open) cardRef.current?.scrollIntoView({ block: "nearest", behavior: "smooth" }); }, [open]);

  const patch = async (body: Record<string, unknown>, msg?: string) => {
    try {
      await api.patch(`/command/goals/${goal.id}`, body);
      if (msg) toast.success(msg);
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't save the goal");
    }
  };

  const remove = async () => {
    const ok = await confirm({ title: "Delete this goal?", description: "Its tasks stay, they just won't point at a goal any more.", confirmLabel: "Delete", danger: true });
    if (!ok) return;
    try {
      await api.delete(`/command/goals/${goal.id}`);
      toast.success("Goal deleted");
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't delete the goal");
    }
  };

  const addTask = async () => {
    const t = newTask.trim();
    if (!t) return;
    setAdding(true);
    try {
      await api.post("/tasks", {
        title: t, goal_id: goal.id, assignee_id: goal.owner_id ?? undefined, source_id: goal.source_id ?? undefined,
        due_at: joinDue(todayYmd(), "", wsTz),
      });
      setNewTask("");
      void loadTasks();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't add that task");
    } finally {
      setAdding(false);
    }
  };

  const toggleTask = async (t: WorkTask) => {
    const done = t.status === "completed";
    setTasks((list) => list?.map((x) => (x.id === t.id ? { ...x, status: done ? "pending" : "completed" } : x)) ?? null);
    try {
      await api.patch(`/tasks/${t.id}`, { status: done ? "pending" : "completed" });
      void loadTasks();
      onChanged();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Couldn't update that task");
      void loadTasks();
    }
  };

  const due = dueLabel(goal.due_date ? `${goal.due_date}T12:00:00` : null);
  const owner = people.find((p) => p.id === goal.owner_id);
  const pct = goal.tasks_total ? goal.tasks_done / goal.tasks_total : 0;
  const active = goal.status === "active";
  const srcOpts = useMemo(() => sourceOptions(sources), [sources]);
  const src = sources.find((s) => s.id === goal.source_id);

  return (
    <div ref={cardRef}>
      <Card className={cn("overflow-hidden transition-colors", open && "cc-glow", !active && "opacity-80")}>
        <button type="button" onClick={onToggleOpen} className="flex w-full items-start gap-3 p-4 text-left hover:bg-zinc-800/15">
          <div className={cn(
            "mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl ring-1",
            goal.status === "done" ? "bg-emerald-500/15 ring-emerald-400/30" : goal.status === "dropped" ? "bg-zinc-800 ring-zinc-700" : "bg-violet-500/12 ring-violet-400/25",
          )}>
            {goal.status === "done" ? <CheckCircle2 className="h-4 w-4 text-emerald-300" /> : goal.status === "dropped" ? <XCircle className="h-4 w-4 text-zinc-500" /> : <Flag className="h-4 w-4 text-violet-300" />}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-0.5">
              <span className={cn("text-[15px] font-semibold", goal.status === "dropped" ? "text-zinc-400 line-through" : "text-zinc-50")}>{goal.title}</span>
              {goal.due_date && (
                <span className={cn("text-xs tabular-nums", !active ? "text-zinc-500" : due.tone === "late" ? "text-rose-300" : due.tone === "today" || due.tone === "soon" ? "text-amber-200" : "text-zinc-400")}>
                  {active ? (due.tone === "late" ? `was due ${dayLabel(goal.due_date)}, ${due.text}` : `by ${dayLabel(goal.due_date, { weekday: "short", month: "short", day: "numeric" })}`) : goal.status === "done" ? "done" : "dropped"}
                </span>
              )}
              {!goal.due_date && active && <span className="text-xs text-amber-300/80">no date yet</span>}
            </div>
            {goal.description && !open && <p className="mt-0.5 line-clamp-1 text-xs text-zinc-500">{goal.description}</p>}
            <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1.5">
              <div className="flex min-w-[160px] flex-1 items-center gap-2 sm:max-w-xs">
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-zinc-800">
                  <div className={cn("h-full rounded-full", pct >= 1 ? "bg-emerald-400" : "cc-brand-bg")} style={{ width: `${goal.tasks_done ? Math.max(4, pct * 100) : 0}%` }} />
                </div>
                <span className="shrink-0 text-[11px] tabular-nums text-zinc-500">
                  {goal.tasks_total ? `${goal.tasks_done} of ${goal.tasks_total} tasks` : "no tasks yet"}
                </span>
              </div>
              {owner && <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-400"><Avatar name={owner.name} email={owner.email} src={owner.image} size="xs" />{firstName(owner.name)}</span>}
              {goal.source_name && <Pill color={src?.color ?? null}><span className={cn("h-1.5 w-1.5 rounded-full", dotClass(src?.color))} />{goal.source_name}</Pill>}
            </div>
          </div>
          <ChevronDown className={cn("mt-2 h-4 w-4 shrink-0 text-zinc-500 transition-transform", open && "rotate-180")} />
        </button>

        {open && (
          <div className="space-y-5 border-t border-zinc-800/70 p-4">
            <div className="grid gap-4 lg:grid-cols-2">
              <div className="space-y-4">
                <Field label="Goal">
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    onBlur={() => { const v = title.trim(); if (v && v !== goal.title) void patch({ title: v }); else setTitle(goal.title); }}
                    onKeyDown={(e) => { if (e.key === "Enter") (e.target as HTMLInputElement).blur(); }}
                    className="h-9 w-full rounded-lg border border-zinc-800 bg-zinc-900/50 px-3 text-sm font-medium text-zinc-100 focus:border-indigo-500/50 focus:outline-none"
                  />
                </Field>
                <Field label="What done looks like">
                  <Textarea
                    value={desc}
                    onChange={(e) => setDesc(e.target.value)}
                    onBlur={() => { if (desc.trim() !== (goal.description ?? "").trim()) void patch({ description: desc.trim() || null }); }}
                    placeholder="In a sentence or two, so anyone can tell when it's reached"
                    className="min-h-[4rem]"
                  />
                </Field>
              </div>
              <div className="space-y-4">
                <Field label="By when">
                  <DayPicker value={goal.due_date ?? ""} onChange={(d) => void patch({ due_date: d || null })} placeholder="Pick the date" className="w-52" />
                </Field>
                <Field label="Who owns it">
                  <PersonPills people={people} value={goal.owner_id} onChange={(id) => void patch({ owner_id: id })} />
                </Field>
                <Field label="Traffic source">
                  <Select value={goal.source_id ?? ""} onChange={(e) => void patch({ source_id: e.target.value || null })}>
                    <option value="">Not tied to one source</option>
                    {srcOpts.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </Select>
                  {goal.source_id && (
                    <Link to={`/traffic/${src?.slug ?? goal.source_id}`} state={{ from: pathname }} className="text-[11px] text-indigo-300 hover:underline">Open {goal.source_name ?? "the source"}</Link>
                  )}
                </Field>
              </div>
            </div>

            <div>
              <div className="mb-1.5 flex items-center gap-2 text-xs font-medium text-zinc-400">
                Its tasks
                {tasks && tasks.length > 0 && <span className="text-zinc-600">{tasks.filter((t) => t.status === "completed").length} of {tasks.length} done</span>}
              </div>
              <div className="overflow-hidden rounded-xl border border-zinc-800/80 bg-zinc-900/30">
                {tasks === null ? (
                  <div className="space-y-2 p-3"><Skeleton className="h-8 w-full" /><Skeleton className="h-8 w-2/3" /></div>
                ) : (
                  <div className="divide-y divide-zinc-800/50">
                    {tasks.map((t) => (
                      <TaskRow key={t.id} t={t} team={team} people={people} wsTz={wsTz} onToggle={() => void toggleTask(t)} onOpen={() => onOpenTask(t)} hideGoal />
                    ))}
                  </div>
                )}
                <form onSubmit={(e) => { e.preventDefault(); void addTask(); }} className={cn("flex items-center gap-2 px-4 py-2", tasks && tasks.length > 0 && "border-t border-zinc-800/50")}>
                  <Plus className="h-4 w-4 shrink-0 text-zinc-500" />
                  <input
                    value={newTask}
                    onChange={(e) => setNewTask(e.target.value)}
                    placeholder={tasks?.length ? "Add another task to this goal" : "Add the first task toward this goal"}
                    className="min-w-0 flex-1 bg-transparent py-1 text-sm text-zinc-100 placeholder:text-zinc-500 focus:outline-none"
                  />
                  {newTask.trim() && <Button type="submit" size="sm" loading={adding}>Add</Button>}
                  <button
                    type="button"
                    onClick={() => onOpenTask({ isNew: true, defaults: { goal_id: goal.id, assignee_id: goal.owner_id, source_id: goal.source_id, title: newTask.trim() } })}
                    className="shrink-0 text-[11px] text-zinc-500 hover:text-zinc-200"
                  >
                    more options
                  </button>
                </form>
              </div>
              <p className="mt-1.5 text-[11px] text-zinc-600">New tasks here start today and belong to {owner ? firstName(owner.name) : "the goal's owner"}. Open one to change it.</p>
            </div>

            <div className="flex flex-wrap items-center gap-2 border-t border-zinc-800/60 pt-3">
              {active ? (
                <>
                  <Button size="sm" variant="secondary" leftIcon={<CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />} onClick={() => void patch({ status: "done" }, "Goal reached")}>Mark reached</Button>
                  <Button size="sm" variant="ghost" leftIcon={<XCircle className="h-3.5 w-3.5" />} onClick={() => void patch({ status: "dropped" }, "Goal dropped")}>Drop it</Button>
                </>
              ) : (
                <Button size="sm" variant="secondary" leftIcon={<RotateCcw className="h-3.5 w-3.5" />} onClick={() => void patch({ status: "active" }, "Goal is active again")}>Make it active again</Button>
              )}
              <Button size="sm" variant="ghost" onClick={remove} leftIcon={<Trash2 className="h-3.5 w-3.5" />} className="ml-auto text-rose-300 hover:bg-rose-500/10 hover:text-rose-200">Delete</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
